import type { ReactNode } from 'react';
import type { ExternalToast } from 'sonner';
import { ApolloError } from '@apollo/client';
import { toast } from 'sonner';

import {
  ESTIMATED_AUM_REQUIRED_CODE,
  PLAN_REVISION_STALE_CODE,
  PLAN_UPDATING_CODE,
  PROFESSIONAL_CONTACT_ACCESS_DENIED_CODE,
  PROFESSIONAL_NETWORK_ACCESS_DENIED_CODE,
  PROFESSIONAL_NETWORK_WRITE_REFUSED_CODE,
  READ_ONLY_ADMIN_WRITE_DENIED_CODE,
} from '@savvy/constants/graphql-error-codes';

export function handleMutationError(error: unknown, fallback: ReactNode, toastOptions?: ExternalToast): void {
  if (isReadOnlyAdminWriteDenied(error)) {
    // Fixed `id` collapses duplicates when multiple mutations on a page throw the same code concurrently.
    toast.error('This account has read-only access and cannot modify data.', {
      id: 'read-only-admin-write-denied',
      position: 'bottom-left',
    });
    return;
  }
  if (hasGraphQLErrorCode(error, PROFESSIONAL_NETWORK_ACCESS_DENIED_CODE)) {
    toast.error("You don't have access to edit this. Ask your team for access.", {
      position: 'bottom-left',
      ...toastOptions,
    });
    return;
  }
  const refusedMessage = graphQLErrorMessageForCode(error, PROFESSIONAL_NETWORK_WRITE_REFUSED_CODE);
  if (refusedMessage) {
    toast.error(refusedMessage, { position: 'bottom-left', ...toastOptions });
    return;
  }
  if (hasGraphQLErrorCode(error, PROFESSIONAL_CONTACT_ACCESS_DENIED_CODE)) {
    toast.error("You don't have access to edit this professional contact. Ask your team for access.", {
      position: 'bottom-left',
      ...toastOptions,
    });
    return;
  }
  const serverMessage = [ESTIMATED_AUM_REQUIRED_CODE, PLAN_REVISION_STALE_CODE, PLAN_UPDATING_CODE]
    .map((code) => graphQLErrorMessageForCode(error, code))
    .find((message) => message !== undefined);
  if (serverMessage) {
    toast.error(serverMessage, { position: 'bottom-left', ...toastOptions });
    return;
  }
  toast.error(fallback, { position: 'bottom-left', ...toastOptions });
}

function isReadOnlyAdminWriteDenied(error: unknown): boolean {
  return hasGraphQLErrorCode(error, READ_ONLY_ADMIN_WRITE_DENIED_CODE);
}

export function hasGraphQLErrorCode(error: unknown, code: string): boolean {
  if (!(error instanceof ApolloError)) return false;
  return error.graphQLErrors.some((e) => e.extensions?.['code'] === code);
}

export function graphQLErrorMessageForCode(error: unknown, code: string): string | undefined {
  if (!(error instanceof ApolloError)) return undefined;
  return error.graphQLErrors.find((e) => e.extensions?.['code'] === code)?.message;
}
